/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2024-11-08 09:41:02                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2025-01-23 13:56:39                               *
 *****************************************************************************/

/* SUMMARY
  * Imports
  * Components
  * Services
  * Styles
  * Prospect: prospects view
    * useEffect: get prospects list
    * useEffect: paginate prospects
    * createVisit: Create new visit event
*/

/* Imports */
import { t } from "i18next";
import { useEffect, useState, useCallback, useMemo } from "react";
import toast from "react-hot-toast";
import { showApiErrorToast } from "../../services/api/error";
/***/

/* Components */
import ProspectsTable from "./sections/ProspectsTable";
import { Pagination } from "../../components/pagination/Pagination";
import EditCreateDrawer from "../../components/edit-create-drawer/EditCreateDrawer";
import ProspectForm, { ProspectFormValidator } from "../../components/data/prospect/ProspectForm";
import FilterDrawer from "../../components/filter-drawer/FilterDrawer";
import ProspectsFilterForm, { getQueriesFromFilter } from "./filter/ProspectsFilterForm";
import FiltersDisplay from "../../components/filter/FiltersDisplay";
import { GenericListLayout } from "../../components/generic-list-view/GenericListLayout";
/***/

/* Hooks */
import { useProspectsFilters } from "../../hooks/filters";
/***/

/* Services */
import { api } from "../../services/api/api";
import { validator } from "../../services/form-validator/form-validator";
import {
  createSingleQuery,
  SearchOptions,
  SearchQuery,
  toSearchOrder,
  searchWithFilters,
} from "../../services/api/search";
import { buildProspectPayload } from "../../services/prospect/prospect";
/***/

/* Styles */
import "./Prospects.scss";
import { prospectExportDescriptor } from "../../services/export/data/prospectExport";
import ExportButton from "../../components/export-button/ExportButton";
import ProspectsCustomerFilterForm from "./filter/ProspectsCustomerFilterForm";
import { useTablePageSize } from "../../hooks/useTablePageSize";
/***/

/**
* Prospect: prospects view
* display prospects list 
*/
export function Prospects({
  isAdminComponent,
  customer,
  hideHeader,
  hideFilters,
  noPadding
}: {
  isAdminComponent?: boolean,
  customer?: EasyTech.Customer,
  hideHeader?: boolean,
  hideFilters?: boolean,
  noPadding?: boolean
}) {
  const [prospectsPage, setProspectsPage] = useState<EasyTech.Prospect[]>([]);
  const [pageState, setPageState] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [orderState, setOrderState] = useState<{ head: string, orderDesc: boolean }>({ head: "date", orderDesc: true });
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const pageSize = useTablePageSize();

  /* Un changement de taille invalide le curseur de page : l'élément 181 est en page 9 à 20 par page
   * et en page 2 à 100. Repartir de la première page est le seul comportement qui ne montre jamais
   * une page vide. */
  useEffect(() => { setPageState(0); }, [pageSize]);

  const onOrderChange = (head: string, orderDesc: boolean) => {
    setOrderState({ head, orderDesc });
  };

  const showBreadcrumb = () => {
    if ((isAdminComponent === undefined || isAdminComponent === false) && !customer)
      return true;
    return false;
  };

  // Fonction pour réinitialiser la page
  const resetPage = useCallback(() => {
    setPageState(0);
  }, []);

  // Utilisation du hook de filtres
  const {
    filters,
    handleFilterSubmit,
    clearFilter,
    clearAllFilters,
    displayFilters,
    isInitialized
  } = useProspectsFilters(resetPage);

  /**
   * Le périmètre que le drawer de filtres ne connaît pas : ouverte depuis la fiche d'un client,
   * cette liste est celle de ses visites. L'export doit recevoir la même restriction, sinon il
   * renvoie les visites de tout le realm depuis une page qui n'en montre qu'une.
   */
  const customerScope: SearchQuery[] = useMemo(
    () => (customer ? [createSingleQuery("customer.id", customer.id)] : []),
    [customer]
  );

  /** Le tri affiché. Une seule définition, pour que l'export ne puisse pas diverger de la table. */
  const searchOrder = useMemo(
    () => toSearchOrder(orderState.head, orderState.orderDesc),
    [orderState]
  );

  /**
  * loadProspectsWithFilters: charger les prospects avec filtres
  */
  const loadProspectsWithFilters = useCallback(async () => {
    if (!filters) return;
    setIsLoading(true);
    try {
      const searchOptions: SearchOptions = {
        paginate: {
          pageSize,
          pageNumber: pageState,
        },
        order: searchOrder,
        queries: [...getQueriesFromFilter(filters), ...customerScope],
      };

      const response = await searchWithFilters<EasyTech.Prospect>(
        "prospects",
        searchOptions
      );

      if (response) {
        setProspectsPage(response.data || []);
        setPageNumber(response.totalPages);
      } else {
        setProspectsPage([]);
        setPageNumber(1);
      }
    } catch (error) {
      console.error("Erreur lors du chargement des prospects avec filtres:", error);
      setProspectsPage([]);
      setPageNumber(1);
    } finally {
      setIsLoading(false);
    }
  }, [searchOrder, filters, pageState, pageSize, customerScope]);

  /**
  * useEffect: paginate prospects
  * reload on page change
  */
  useEffect(() => {
    if (isInitialized) {
      loadProspectsWithFilters();
    }
  }, [loadProspectsWithFilters, isInitialized]);
  /***/

  /**
  * createVisit: Create new visit event
  * @param data - visit data 
  */
  const createVisit = (data: EasyTech.Prospect): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (validator.isFormValid(data, ProspectFormValidator)) {
        const payload = buildProspectPayload(data);

        api.post("users/prospects", payload).then((ret) => {
          toast.success("Visit created !");
          loadProspectsWithFilters();
          return resolve();
        }).catch((err) => {
          showApiErrorToast(err);
          return reject(err);
        });
      } else {
        const fieldNames = {
          customer: t("common:customer"),
          contact: t("common:contact"),
          technician: t("common:technician"),
          date: t("common:date:label")
        };

        const errorMessage = validator.getFormErrorMessage(data, ProspectFormValidator, fieldNames, t);
        toast.error(errorMessage);
        return reject();
      }
    });
  };
  /***/

  // Actions d'en-tête
  const headerActions = (
    <>
      <ExportButton<EasyTech.Prospect> filters={filters} entityName="prospects"
        fileName="prospects.xlsx"
        descriptor={prospectExportDescriptor} getQueryFromFilter={getQueriesFromFilter}
        order={searchOrder} extraQueries={customerScope}
      />

      <EditCreateDrawer<EasyTech.Prospect>
        data={customer !== undefined ? ({ customer: customer } as EasyTech.Prospect) : undefined}
        FormComponent={ProspectForm}
        entityName="visits"
        onSubmit={createVisit}
        isNotEditable={true}
      />
    </>
  );

  // Composant du drawer de filtre
  const filterDrawerComponent = (
    <FilterDrawer<EasyTech.ProspectsFilterType>
      FormComponent={customer !== undefined ? ProspectsCustomerFilterForm : ProspectsFilterForm}
      onSubmit={(data) => {
        // TODO: Refacto - Cette logique particulière de validation de date de RangeDatePicker a été ajoutée ici pour pallier le fait que le form est soumis depuis le bouton du Parent (FilterDrawer) sans déclencher la validation du formulaire de manière native.
        if (data.startDate && !data.endDate) return Promise.reject();
        if (data.endDate && !data.startDate) return Promise.reject();
        return handleFilterSubmit(data);
      }}
      entityName={t("common:visits")}
      data={filters}
    />
  );

  const filterBadgesComponent = (
    <FiltersDisplay
      filters={displayFilters}
      onClearFilter={(field, newValue) => {
        // Quand on supprime le badge dateRange, on supprime startDate et endDate
        if (field === "dateRange") {
          clearFilter("startDate", null);
          clearFilter("endDate", null);
          return;
        }
        clearFilter(field, newValue);
      }}
      onClearAll={clearAllFilters}
      size="sm"
      compact={true}
      entity="prospect"
    />
  );

  return (
    <GenericListLayout
      entityName={t("common:visits")}
      path="/visits"
      isLoading={isLoading}
      headerActions={hideFilters ? null : headerActions}
      filterDrawer={hideFilters ? null : filterDrawerComponent}
      filterBadges={hideFilters ? null : filterBadgesComponent}
      content={<ProspectsTable prospects={prospectsPage} onHeadClick={onOrderChange}
        onChange={loadProspectsWithFilters}
        selectedHead={orderState.head} orderDesc={orderState.orderDesc}
        customer={customer}
        isAdminComponent={isAdminComponent} />}
      pagination={
        <Pagination
          actualPage={pageState}
          pageNumber={pageNumber}
          changePage={setPageState}
        />
      }
      className="prospects"
      showBreadcrumb={showBreadcrumb()}
      hideHeader={hideHeader}
      noPadding={noPadding}
    />
  );
}
/***/
